/**
 * Resource Client
 * Handles CRUD operations (list, get, create, update, delete)
 */

import type {
  FrappeListParams,
  FrappeGetParams,
  FrappeCreateParams,
  FrappeUpdateParams,
  FrappeDeleteParams,
} from "./types";
import type { FrappeListResponse } from "../response/types";
import { ResponseHandler } from "../response/ResponseHandler";
import { logger } from "@/lib/utils/logger";

export interface ResourceClientConfig {
  url: string;
  getAuthHeaders: () => Promise<HeadersInit>;
  getCount?: (doctype: string, filters?: Record<string, any>) => Promise<number>;
}

export class ResourceClient {
  private config: ResourceClientConfig;
  private responseHandler: ResponseHandler;

  constructor(config: ResourceClientConfig) {
    this.config = config;
    this.responseHandler = new ResponseHandler();
  }

  private getApiUrl(endpoint: string): string {
    const baseUrl = this.config.url.replace(/\/$/, "");
    // If baseUrl is "/api" (Vite proxy), don't add another "/api" prefix
    // If baseUrl is absolute (http://...), add "/api" prefix
    const apiPrefix = baseUrl === "/api" ? "" : "/api";
    return `${baseUrl}${apiPrefix}/resource/${endpoint}`;
  }

  /**
   * List documents of a doctype
   * @returns Array of documents (legacy method for backward compatibility)
   */
  async list<T = any>(params: FrappeListParams): Promise<T[]> {
    const result = await this.listWithPagination<T>(params);
    return result.data;
  }

  /**
   * List documents of a doctype with pagination metadata
   * @returns Response with data, total count, and page length
   */
  async listWithPagination<T = any>(
    params: FrappeListParams
  ): Promise<FrappeListResponse<T>> {
    const { doctype, filters, or_filters, fields, limit_start, limit_page_length, order_by } =
      params;

    const queryParams = new URLSearchParams();
    if (filters) {
      queryParams.append("filters", JSON.stringify(filters));
    }
    if (or_filters) {
      queryParams.append("or_filters", JSON.stringify(or_filters));
    }
    if (fields) {
      queryParams.append("fields", JSON.stringify(fields));
    }
    if (limit_start !== undefined) {
      queryParams.append("limit_start", limit_start.toString());
    }
    if (limit_page_length !== undefined) {
      queryParams.append("limit_page_length", limit_page_length.toString());
    }
    if (order_by) {
      queryParams.append("order_by", order_by);
    }
    // Removed assigned_to query parameter - now using __assigned_to in filters

    const url = `${this.getApiUrl(doctype)}?${queryParams.toString()}`;
    const headers = await this.config.getAuthHeaders();

    // DEBUG: Log the actual API request
    if (filters || or_filters) {
      console.log("[ResourceClient] API Request:", {
        url: url.substring(0, 200) + (url.length > 200 ? "..." : ""),
        filters: filters ? JSON.stringify(filters).substring(0, 500) : undefined,
        or_filters: or_filters ? JSON.stringify(or_filters).substring(0, 500) : undefined,
      });
    }

    const response = await fetch(url, {
      method: "GET",
      headers,
      credentials: "include", // Critical: must include for cross-origin cookies
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      logger.error("Frappe list error:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText.substring(0, 500), // Limit error text length
      });
    }

    // Extract filters for getCount if needed
    const filtersForCount =
      filters && !Array.isArray(filters) ? filters : undefined;

    return this.responseHandler.handleListResponse<T>(
      response,
      limit_page_length,
      doctype,
      this.config.getCount,
      filtersForCount
    );
  }

  /**
   * Get a single document
   */
  async get<T = any>(params: FrappeGetParams): Promise<T> {
    const { doctype, name } = params;
    const url = this.getApiUrl(`${doctype}/${name}`);

    const headers = await this.config.getAuthHeaders();

    const response = await fetch(url, {
      method: "GET",
      headers,
      credentials: "include",
    });

    return this.responseHandler.handleResponse<T>(response);
  }

  /**
   * Create a new document
   */
  async create<T = any>(params: FrappeCreateParams): Promise<T> {
    const { doctype, data } = params;
    const url = this.getApiUrl(doctype);

    const headers = await this.config.getAuthHeaders();

    const response = await fetch(url, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(data),
    });

    return this.responseHandler.handleResponse<T>(response);
  }

  /**
   * Update an existing document
   */
  async update<T = any>(params: FrappeUpdateParams): Promise<T> {
    const { doctype, name, data } = params;
    const url = this.getApiUrl(`${doctype}/${name}`);

    const headers = await this.config.getAuthHeaders();

    // Debug logging for patch saves
    if (import.meta.env.DEV) {
      console.log('[API] update item request:', {
        doctype,
        name,
        url,
        patchKeys: Object.keys(data),
        patch: data,
      });
    }

    const response = await fetch(url, {
      method: "PUT",
      headers,
      credentials: "include",
      body: JSON.stringify(data),
    });

    const result = await this.responseHandler.handleResponse<T>(response);

    // Debug logging for response
    if (import.meta.env.DEV) {
      console.log('[API] update item response:', {
        doctype,
        name,
        responseKeys: result && typeof result === 'object' ? Object.keys(result) : [],
        response: result,
      });
    }

    return result;
  }

  /**
   * Delete a document
   */
  async delete(params: FrappeDeleteParams): Promise<void> {
    const { doctype, name } = params;
    const url = this.getApiUrl(`${doctype}/${name}`);

    const headers = await this.config.getAuthHeaders();

    const response = await fetch(url, {
      method: "DELETE",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: response.statusText,
      }));
      throw new Error(error.message || `HTTP error! status: ${response.status}`);
    }
  }
}

